import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Dropdown, Modal } from "react-bootstrap";
import PerfectScrollbar from "react-perfect-scrollbar";
import LogoutPage from "./Logout";
import profileImage from "../../../images/user.jpg";
import UsersService from "../../../services/UsersService";
import UserAssignmentsService from "../../../services/UserAssignmentsService";
import AuthService from "../../../services/AuthService";
import LanguageSwitcher from "../../components/LanguageSwitcher/LanguageSwitcher";
import BilingualText from "../../components/BilingualText/BilingualText";
import swal from "sweetalert";
const Header = ({ onNote }) => {
	const [profile, setProfile] = useState(null);
	const [loading, setLoading] = useState(false);
	const [headerFix, setheaderFix] = useState(false);
	const [assignments, setAssignments] = useState([]);
	const [rawAssignments, setRawAssignments] = useState([]);
	const [userType, setUserType] = useState({
		SUPERADMIN: "Super Admin",
		ADMIN: "Admin",
		STAFF: "Staff",
		CASHIER: "Cashier",
		ACCOUNTANT: "Accountant",
	});
	const [currentUserType, setCurrentUserType] = useState("");
	const [loadingAssignments, setLoadingAssignments] = useState(false);
	const [showCompanyBranchModal, setShowCompanyBranchModal] = useState(false);
	const [assignmentsLoaded, setAssignmentsLoaded] = useState(false);
	const [selectedContext, setSelectedContext] = useState({
		companyId: null,
		companyName: "Select Company",
		branchId: null,
		branchName: "Select Branch",
		assignmentId: null,
	});

	// Fullscreen state
	const [isFullscreen, setIsFullscreen] = useState(
		Boolean(document.fullscreenElement),
	);
	// Theme state: 'dark' or 'light'
	const [theme, setTheme] = useState(() => {
		try {
			const saved = localStorage.getItem("data-theme-version");
			if (saved) return saved;
			const bodyAttr = document?.body?.getAttribute("data-theme-version");
			return bodyAttr || "dark";
		} catch (e) {
			return "dark";
		}
	});

	useEffect(() => {
		try {
			document.body.setAttribute("data-theme-version", theme);
			localStorage.setItem("data-theme-version", theme);
		} catch (e) {}
	}, [theme]);

	const toggleFullscreen = (e) => {
		e && e.preventDefault();
		try {
			if (!document.fullscreenElement) {
				document.documentElement.requestFullscreen &&
					document.documentElement.requestFullscreen();
			} else {
				document.exitFullscreen && document.exitFullscreen();
			}
		} catch (err) {
			console.error("Fullscreen toggle failed", err);
		}
	};

	useEffect(() => {
		const onFullscreenChange = () =>
			setIsFullscreen(Boolean(document.fullscreenElement));
		document.addEventListener("fullscreenchange", onFullscreenChange);
		return () =>
			document.removeEventListener("fullscreenchange", onFullscreenChange);
	}, []);
	useEffect(() => {
		const fetchProfile = async () => {
			setLoading(true);
			try {
				const res = await UsersService.getUserProfile();
				if (res && res.success) {
					setProfile(res.data);
					// Fetch assignments after getting profile
					fetchAssignments(res.data.id);
				}
			} catch (err) {
				console.error("Failed to fetch profile", err);
			} finally {
				setLoading(false);
			}
		};
		fetchProfile();
	}, []);

	const fetchAssignments = async (userId) => {
		setLoadingAssignments(true);
		try {
			const res = await UserAssignmentsService.getContextSwitchingData(userId);
			if (res && res.success) {
				setAssignments(res.data);
				setRawAssignments(res.rawAssignments);
				const userDetails = JSON.parse(localStorage.getItem("userDetails"));
				const savedContext = localStorage.getItem("userContext");
				if (savedContext) {
					setSelectedContext(JSON.parse(savedContext));
					setCurrentUserType(JSON.parse(savedContext)?.roleCode || "");
				} else if (res.rawAssignments && res.rawAssignments.length > 0) {
					const primaryAssignment =
						res.rawAssignments.find((a) => a.isPrimary) ||
						res.rawAssignments.find(
							(a) => a.companyId === userDetails.user.currentContext.companyId,
						);
					const defaultContext = {
						companyId: primaryAssignment.companyId,
						companyName: primaryAssignment.company?.name || "Unknown Company",
						branchId: primaryAssignment.branchId,
						branchName: primaryAssignment.branch?.branchName || "",
						assignmentId: primaryAssignment.id,
						roleCode: primaryAssignment.role?.roleCode || "",
					};
					setSelectedContext(defaultContext);
					setCurrentUserType(userType[primaryAssignment.role?.roleCode] || "");
					localStorage.setItem("userContext", JSON.stringify(defaultContext));
				}
			}
		} catch (err) {
			swal("Error", "Failed to load company/branch assignments", "error");
		} finally {
			setLoadingAssignments(false);
			setAssignmentsLoaded(true);
		}
	};

	const handleContextSwitch = async (company, branch, type) => {
		// Prefer the explicit `branch` argument when available; fallback to rawAssignments lookup
		const getCompanyData =
			rawAssignments.find((list) => list.companyId == company.companyId) || {};
		const userDetails = JSON.parse(localStorage.getItem("userDetails"));
		const newContext = {
			companyId: company.companyId || getCompanyData.companyId,
			companyName: company.companyName || getCompanyData?.company?.name,
			branchId:
				branch?.branchId ||
				getCompanyData?.branchId ||
				getCompanyData?.branch?.branchId ||
				null,
			// Ensure branchName is a string, not the full branch object
			branchName:
				branch?.branchName ||
				getCompanyData?.branch?.branchName ||
				getCompanyData?.branch ||
				null,
			assignmentId: getCompanyData.id,
			roleCode: getCompanyData.role?.roleCode || "",
		};
		try {
			swal({
				title: `Switching ${branch && branch.branchName ? "branch" : "company"}...`,
				text: `Switching to ${company.companyName}${
					branch?.branchName ? " - " + branch.branchName : ""
				}`,
				icon: "info",
				buttons: false,
				closeOnClickOutside: false,
				closeOnEsc: false,
			});
			let response;
			if (userDetails.user.activeRole.roleCode === "SUPERADMIN") {
				if (type === "branch") {
					response = await AuthService.switchBranch(branch.branchId);
				} else {
					response = await AuthService.switchCompany(company.companyId);
				}
			} else if (
				["ADMIN", "STAFFS", "CASHIER", "HEADOFFICE"].includes(
					userDetails.user.activeRole.roleCode,
				)
			) {
				response = await AuthService.switchBranch(branch.branchId);
			} else {
				throw new Error("Invalid role code");
			}

			if (response.success) {
				let updateUserDetails = JSON.parse(localStorage.getItem("userDetails"));
				updateUserDetails.user.currentContext = {
					branchId: branch?.branchId || null,
					branchName: branch?.branchName || null,
					companyId: company.companyId,
					companyName: company.companyName,
				};
				localStorage.setItem("userDetails", JSON.stringify(updateUserDetails));
				AuthService.handleAuthResponse(response);
				setSelectedContext(newContext);
				localStorage.setItem("userContext", JSON.stringify(newContext));
				swal({
					title: `Switched ${branch && branch.branchName ? "branch" : "company"}!`,
					text: `Successfully switched to ${company.companyName}${
						branch?.branchName ? " - " + branch?.branchName : ""
					}`,
					icon: "success",
					timer: 2000,
					buttons: false,
				}).then(() => {
					window.location.href = "/dashboard";
				});
			} else {
				throw new Error(response.message || "Failed to switch context");
			}
		} catch (error) {
			swal({
				title: "Switch Failed",
				text:
					error.response?.data?.message ||
					error.message ||
					"Failed to switch context. Please try again.",
				icon: "error",
				button: "OK",
			});
		}
	};

	useEffect(() => {
		window.addEventListener("scroll", () => {
			setheaderFix(window.scrollY > 50);
		});
	}, []);

	return (
		<div className={`header ${headerFix ? "sticky" : ""}`}>
			{/* Modal popup for SUPERADMIN when no branch selected (forces selection) */}
			{(() => {
				try {
					const userDetails = JSON.parse(
						localStorage.getItem("userDetails") || "{}",
					);
					const isSuperAdmin =
						userDetails?.user?.activeRole?.roleCode === "SUPERADMIN";
					const showModal =
						assignmentsLoaded && isSuperAdmin && !selectedContext?.branchId;
					return (
						<Modal
							show={!!showModal}
							backdrop="static"
							keyboard={false}
							centered
							size="lg"
						>
							<Modal.Header>
								<Modal.Title>Select Branch</Modal.Title>
							</Modal.Header>
							<Modal.Body>
								<div style={{ fontSize: 13, color: "#666", marginBottom: 8 }}>
									Choose one of the branches below
								</div>
								<div
									style={{
										display: "grid",
										gridTemplateColumns: "1fr 1fr",
										gap: 16,
									}}
								>
									{assignments && assignments.length > 0 ? (
										assignments.map((company, cidx) => (
											<div
												key={company.companyId ?? `company-${cidx}`}
												style={{
													border: "1px solid #f0f0f0",
													borderRadius: 8,
													padding: 12,
													background: "#fafafa",
												}}
											>
												<div style={{ fontWeight: 700, marginBottom: 8 }}>
													{company.companyName}
												</div>
												{company.branches && company.branches.length > 0 ? (
													company.branches.map((branch, bidx) => (
														<div
															key={branch.branchId ?? `branch-${bidx}`}
															onClick={(e) => {
																e.stopPropagation();
																handleContextSwitch(company, branch, "branch");
															}}
															style={{
																cursor: "pointer",
																padding: 10,
																borderRadius: 6,
																background: "#fff",
																marginBottom: 8,
																boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
															}}
														>
															<div style={{ fontWeight: 600 }}>
																{branch.branchName}
															</div>
															<div style={{ fontSize: 12, color: "#666" }}>
																Branch ID: {branch.branchId}
															</div>
														</div>
													))
												) : (
													<div className="text-muted">No branches</div>
												)}
											</div>
										))
									) : (
										<div className="text-muted">No assignments available</div>
									)}
								</div>
							</Modal.Body>
						</Modal>
					);
				} catch (e) {
					return null;
				}
			})()}
			<div className="header-content">
				<nav className="navbar navbar-expand">
					<div className="collapse navbar-collapse justify-content-between">
						<div className="header-left"></div>
						<ul className="navbar-nav header-right">
							{/* Fullscreen Toggle */}
							<li className="nav-item">
								<button
									className="btn btn-info btn-sm me-3"
									onClick={(e) => toggleFullscreen(e)}
									title={isFullscreen ? "Exit Fullscreen" : "Full Screen"}
								>
									<i
										className={`fa ${isFullscreen ? "fa-compress" : "fa-expand"}`}
									/>
								</button>
							</li>

							{/* EMI Calculator (icon-only, like fullscreen) */}
							<li className="nav-item">
								<Link
									to="/emi-calculator"
									className="btn btn-dark btn-sm me-3"
									title="EMI Calculator"
								>
									<i className="fa fa-calculator" />
								</Link>
							</li>

							{/* Theme Toggle */}
							<li className="nav-item">
								<a
									className="btn btn-secondary btn-sm me-3"
									onClick={() =>
										setTheme((prev) => (prev === "dark" ? "light" : "dark"))
									}
									title={
										theme === "dark"
											? "Switch to light theme"
											: "Switch to dark theme"
									}
								>
									<i
										className={`fa ${theme === "dark" ? "fa-sun" : "fa-moon"}`}
									/>
								</a>
							</li>

							<li className="nav-item">
								<button
									className="btn btn-info btn-sm me-3"
									onClick={() => onNote()}
									title={
										theme === "dark"
											? "Switch to light theme"
											: "Switch to dark theme"
									}
								>
									<i
										className="fa fa-bolt"
										style={{ fontSize: 18, color: "#fff", width: "26px" }}
										aria-hidden="true"
									/>
								</button>
							</li>

							{/* Language Switcher */}
							<li className="nav-item">
								<LanguageSwitcher />
							</li>

							{/* Company / Branch Switcher Modal */}
							<li className="nav-item dropdown notification_dropdown">
								<a
									className="nav-link"
									onClick={() => setShowCompanyBranchModal(true)}
								>
									<div className="d-flex align-items-center">
										<div className="text-start d-none d-md-block">
											<small
												className="d-block text-muted"
												style={{ fontSize: "8px" }}
											>
												{selectedContext.companyName}
											</small>
											<strong style={{ fontSize: "12px" }}>
												{selectedContext.branchName}
											</strong>
										</div>
										<i className="fa fa-angle-down ms-2"></i>
									</div>
								</a>
							</li>

							{/* Company / Branch Selection Modal */}
							<Modal
								show={showCompanyBranchModal}
								onHide={() => setShowCompanyBranchModal(false)}
								backdrop="static"
								keyboard={false}
								centered
								size="lg"
							>
								<Modal.Header closeButton>
									<Modal.Title>Switch Company / Branch</Modal.Title>
								</Modal.Header>
								<Modal.Body>
									<div
										style={{ fontSize: 13, color: "#666", marginBottom: 16 }}
									>
										Select your working context
									</div>
									{loadingAssignments ? (
										<div className="text-center py-3">
											<div
												className="spinner-border spinner-border-sm text-primary"
												role="status"
											>
												<span className="visually-hidden">Loading...</span>
											</div>
											<p className="text-muted mt-2 mb-0">
												Loading assignments...
											</p>
										</div>
									) : assignments.length === 0 ? (
										<div className="text-center py-3">
											<i
												className="las la-exclamation-circle"
												style={{ fontSize: 48, color: "#ccc" }}
											></i>
											<p className="text-muted mb-0">No assignments found</p>
										</div>
									) : (
										<div
											style={{
												display: "grid",
												gridTemplateColumns: "1fr 1fr",
												gap: 16,
											}}
										>
											{assignments.map((company, cidx) => (
												<div
													key={company.companyId ?? `company-${cidx}`}
													style={{
														border: "1px solid #f0f0f0",
														borderRadius: 8,
														padding: 12,
														background: "#fafafa",
													}}
												>
													<div
														style={{
															fontWeight: 700,
															marginBottom: 12,
															display: "flex",
															alignItems: "center",
														}}
													>
														<i className="las la-building me-2 text-primary"></i>
														{company.companyName}
													</div>
													{company.branches && company.branches.length > 0 ? (
														company.branches.map((branch, bidx) => {
															const isSelected =
																selectedContext?.branchId === branch.branchId;
															return (
																<div
																	key={branch.branchId ?? `branch-${bidx}`}
																	onClick={(e) => {
																		e.stopPropagation();
																		handleContextSwitch(
																			company,
																			branch,
																			"branch",
																		);
																		setShowCompanyBranchModal(false);
																	}}
																	style={{
																		cursor: "pointer",
																		padding: 10,
																		borderRadius: 6,
																		background: isSelected ? "#e7f1ff" : "#fff",
																		marginBottom: 8,
																		boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
																		border: isSelected
																			? "2px solid #0d6efd"
																			: "none",
																		display: "flex",
																		alignItems: "center",
																		justifyContent: "space-between",
																	}}
																>
																	<div>
																		<div
																			style={{ fontWeight: 600, color: "#333" }}
																		>
																			{branch.branchName}
																		</div>
																		<div
																			style={{ fontSize: 12, color: "#666" }}
																		>
																			Branch ID: {branch.branchId}
																			{branch.isPrimary && (
																				<span
																					style={{ marginLeft: 8 }}
																					className="badge bg-primary text-white"
																				>
																					Primary
																				</span>
																			)}
																		</div>
																	</div>
																	{isSelected && (
																		<i
																			className="las la-check-circle text-primary"
																			style={{ fontSize: 20 }}
																		></i>
																	)}
																</div>
															);
														})
													) : (
														<div className="text-muted">No branches</div>
													)}
												</div>
											))}
										</div>
									)}
								</Modal.Body>
							</Modal>

							<li className="nav-item ">
								<Dropdown className="dropdown header-profile2">
									<Dropdown.Toggle
										variant=""
										as="a"
										className="nav-link i-false c-pointer"
									>
										<div className="header-info2 d-flex align-items-center">
											<div className="d-flex align-items-center sidebar-info">
												<div>
													<h4 className="mb-0">{profile?.name}</h4>
													<span className="d-block text-end">
														{currentUserType}
													</span>
												</div>
											</div>
											<img src={profileImage} alt="" />
										</div>
									</Dropdown.Toggle>
									<Dropdown.Menu
										align="right"
										className="mt-3 dropdown-menu dropdown-menu-end"
									>
										<Link to="/profile" className="dropdown-item ai-icon">
											<svg
												xmlns="http://www.w3.org/2000/svg"
												width="24px"
												height="24px"
												viewBox="0 0 24 24"
												version="1.1"
												className="svg-main-icon"
											>
												<g
													stroke="none"
													strokeWidth="1"
													fill="none"
													fillRule="evenodd"
												>
													<polygon points="0 0 24 0 24 24 0 24" />
													<path
														d="M12,11 C9.790861,11 8,9.209139 8,7 C8,4.790861 9.790861,3 12,3 C14.209139,3 16,4.790861 16,7 C16,9.209139 14.209139,11 12,11 Z"
														fill="#000000"
														fillRule="nonzero"
														opacity="0.3"
													/>
													<path
														d="M3.00065168,20.1992055 C3.38825852,15.4265159 7.26191235,13 11.9833413,13 C16.7712164,13 20.7048837,15.2931929 20.9979143,20.2 C21.0095879,20.3954741 20.9979143,21 20.2466999,21 C16.541124,21 11.0347247,21 3.72750223,21 C3.47671215,21 2.97953825,20.45918 3.00065168,20.1992055 Z"
														fill="var(--primary)"
														fillRule="nonzero"
													/>
												</g>
											</svg>
											<span className="ms-2">Profile </span>
										</Link>
										<Link
											to="/branches/settings"
											className="dropdown-item ai-icon"
										>
											<svg
												xmlns="http://www.w3.org/2000/svg"
												width="24px"
												height="24px"
												viewBox="0 0 24 24"
												version="1.1"
												class="svg-main-icon"
											>
												<g
													stroke="none"
													stroke-width="1"
													fill="none"
													fill-rule="evenodd"
												>
													<rect x="0" y="0" width="24" height="24"></rect>
													<path
														d="M18.6225,9.75 L18.75,9.75 C19.9926407,9.75 21,10.7573593 21,12 C21,13.2426407 19.9926407,14.25 18.75,14.25 L18.6854912,14.249994 C18.4911876,14.250769 18.3158978,14.366855 18.2393549,14.5454486 C18.1556809,14.7351461 18.1942911,14.948087 18.3278301,15.0846699 L18.372535,15.129375 C18.7950334,15.5514036 19.03243,16.1240792 19.03243,16.72125 C19.03243,17.3184208 18.7950334,17.8910964 18.373125,18.312535 C17.9510964,18.7350334 17.3784208,18.97243 16.78125,18.97243 C16.1840792,18.97243 15.6114036,18.7350334 15.1896699,18.3128301 L15.1505513,18.2736469 C15.008087,18.1342911 14.7951461,18.0956809 14.6054486,18.1793549 C14.426855,18.2558978 14.310769,18.4311876 14.31,18.6225 L14.31,18.75 C14.31,19.9926407 13.3026407,21 12.06,21 C10.8173593,21 9.81,19.9926407 9.81,18.75 C9.80552409,18.4999185 9.67898539,18.3229986 9.44717599,18.2361469 C9.26485393,18.1556809 9.05191298,18.1942911 8.91533009,18.3278301 L8.870625,18.372535 C8.44859642,18.7950334 7.87592081,19.03243 7.27875,19.03243 C6.68157919,19.03243 6.10890358,18.7950334 5.68746499,18.373125 C5.26496665,17.9510964 5.02757002,17.3784208 5.02757002,16.78125 C5.02757002,16.1840792 5.26496665,15.6114036 5.68716991,15.1896699 L5.72635306,15.1505513 C5.86570889,15.008087 5.90431906,14.7951461 5.82064513,14.6054486 C5.74410223,14.426855 5.56881236,14.310769 5.3775,14.31 L5.25,14.31 C4.00735931,14.31 3,13.3026407 3,12.06 C3,10.8173593 4.00735931,9.81 5.25,9.81 C5.50008154,9.80552409 5.67700139,9.67898539 5.76385306,9.44717599 C5.84431906,9.26485393 5.80570889,9.05191298 5.67216991,8.91533009 L5.62746499,8.870625 C5.20496665,8.44859642 4.96757002,7.87592081 4.96757002,7.27875 C4.96757002,6.68157919 5.20496665,6.10890358 5.626875,5.68746499 C6.04890358,5.26496665 6.62157919,5.02757002 7.21875,5.02757002 C7.81592081,5.02757002 8.38859642,5.26496665 8.81033009,5.68716991 L8.84944872,5.72635306 C8.99191298,5.86570889 9.20485393,5.90431906 9.38717599,5.82385306 L9.49484664,5.80114977 C9.65041313,5.71688974 9.7492905,5.55401473 9.75,5.3775 L9.75,5.25 C9.75,4.00735931 10.7573593,3 12,3 C13.2426407,3 14.25,4.00735931 14.25,5.25 L14.249994,5.31450877 C14.250769,5.50881236 14.366855,5.68410223 14.552824,5.76385306 C14.7351461,5.84431906 14.948087,5.80570889 15.0846699,5.67216991 L15.129375,5.62746499 C15.5514036,5.20496665 16.1240792,4.96757002 16.72125,4.96757002 C17.3184208,4.96757002 17.8910964,5.20496665 18.312535,5.626875 C18.7350334,6.04890358 18.97243,6.62157919 18.97243,7.21875 C18.97243,7.81592081 18.7350334,8.38859642 18.3128301,8.81033009 L18.2736469,8.84944872 C18.1342911,8.99191298 18.0956809,9.20485393 18.1761469,9.38717599 L18.1988502,9.49484664 C18.2831103,9.65041313 18.4459853,9.7492905 18.6225,9.75 Z"
														fill="#000000"
														fill-rule="nonzero"
														opacity="0.3"
													></path>
													<path
														d="M12,15 C13.6568542,15 15,13.6568542 15,12 C15,10.3431458 13.6568542,9 12,9 C10.3431458,9 9,10.3431458 9,12 C9,13.6568542 10.3431458,15 12,15 Z"
														fill="#000000"
													></path>
												</g>
											</svg>
											<span className="ms-2">Branch Settings</span>
										</Link>
										<LogoutPage />
									</Dropdown.Menu>
								</Dropdown>
							</li>
						</ul>
					</div>
				</nav>
			</div>
		</div>
	);
};

export default Header;
